import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { indianStates } from '../Components/states';

import { API_URL } from '../config/api';

const EmployeeSelf = () => {
  const navigate = useNavigate();
  const email = localStorage.getItem('email') || '';
  const [activeTab, setActiveTab] = useState('basic');
  const [validatedTabs, setValidatedTabs] = useState(['basic']); // Track which tabs have been validated
  const [loading, setLoading] = useState(false);
  const [ifscValidating, setIfscValidating] = useState(false);
  const [formData, setFormData] = useState({
    // Basic Info
    firstName: '',
    middleName: '',
    lastName: '',
    gender: '',
    dateOfBirth: '',
    bloodGroup: '',
    citizenship: '',
    email: email,
    mobileNumber: '',
    countryCode: '+91',
    maritalStatus: '',
    aadharNumber: '',
    panNumber: '',
    // Address
    addresses: [],
    // Experience
    experiences: [],
    // Education
    educationDetails: [],
    // Bank
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    branchName: '',
    accountHolderName: '',
    // Document
    aadharCard: null,
    panCard: null,
    educationCertificates: []
  });
  const [errors, setErrors] = useState({});
  const [uploadingFiles, setUploadingFiles] = useState({
    aadharCard: false,
    panCard: false,
    educationCertificates: {}
  });
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressData, setAddressData] = useState({
    permanent: {
      address1: '',
      address2: '',
      country: 'India',
      state: 'Tamil Nadu',
      city: '',
      pincode: ''
    },
    present: {
      address1: '',
      address2: '',
      country: 'India',
      state: 'Tamil Nadu',
      city: '',
      pincode: ''
    },
    sameAsPermanent: false
  });
  const [addressErrors, setAddressErrors] = useState({});
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [stateSearchTerm, setStateSearchTerm] = useState('');
  const [activeStateDropdown, setActiveStateDropdown] = useState(null); // 'permanent' or 'present'
  const stateDropdownRef = useRef(null);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  const [activeCountryDropdown, setActiveCountryDropdown] = useState(null); // 'permanent' or 'present'
  const countryDropdownRef = useRef(null);

  const countries = ['India', 'USA', 'UK'];

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (stateDropdownRef.current && !stateDropdownRef.current.contains(event.target)) {
        setShowStateDropdown(false);
        setStateSearchTerm('');
        setActiveStateDropdown(null);
      }
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
        setShowCountryDropdown(false);
        setCountrySearchTerm('');
        setActiveCountryDropdown(null);
      }
    };

    if (showStateDropdown || showCountryDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStateDropdown, showCountryDropdown]);

  const tabs = [
    { id: 'basic', label: 'Basic Info' },
    { id: 'experience', label: 'Experience' },
    { id: 'education', label: 'Education' },
    { id: 'bank', label: 'Bank' },
    { id: 'document', label: 'Document' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    let validatedValue = value;

    // Validate Aadhar Number - only numeric, no alphabets, max 12 digits
    if (name === 'aadharNumber') {
      validatedValue = value.replace(/\D/g, '').slice(0, 12);
    }

    // Validate Account Number - only numeric, no alphabets
    if (name === 'accountNumber') {
      validatedValue = value.replace(/\D/g, '');
    }

    // IFSC uppercase
    if (name === 'ifscCode') {
      validatedValue = value.toUpperCase();
    }

    // Validate Bank Name - only alphabets and spaces, no numeric
    if (name === 'bankName') {
      validatedValue = value.replace(/[^a-zA-Z\s]/g, '');
    }

    // Validate Account Holder Name - only alphabets and spaces, no numeric
    if (name === 'accountHolderName') {
      validatedValue = value.replace(/[^a-zA-Z\s]/g, '');
    }

    // Validate Date fields - ensure year is 4 digits (same as Holiday.js)
    if (name === 'dateOfBirth') {
      if (value) {
        const parts = value.split('-');
        if (parts.length === 3 && parts[0].length !== 4) {
          // Year must be 4 digits - don't update state
          return;
        }
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: validatedValue
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleMobileNumberChange = (e) => {
    const value = e.target.value;
    // Only allow numeric characters and limit to 10 digits
    const numericValue = value.replace(/\D/g, '').slice(0, 10);
    setFormData(prev => ({
      ...prev,
      mobileNumber: numericValue
    }));
    // Clear error when user starts typing
    if (errors.mobileNumber) {
      setErrors(prev => ({
        ...prev,
        mobileNumber: ''
      }));
    }
  };

  // Validate IFSC and auto-fill branch/bank
  const validateIfscCode = async (code) => {
    const ifsc = (code || '').trim();
    if (!ifsc || !API_URL) {
      toast.error('Please enter a valid IFSC code');
      return;
    }

    try {
      setIfscValidating(true);
      const payload = {
        action: 'validate-ifsc',
        ifscCode: ifsc
      };
      const response = await axios.post(API_URL, payload, {
        headers: { 'Content-Type': 'application/json' }
      });
      const result = response.data;
      if (result?.success && result?.data) {
        const branch = result.data.branch || '';
        const bank = result.data.bank || '';
        setFormData(prev => ({
          ...prev,
          branchName: branch || prev.branchName,
          bankName: prev.bankName || bank
        }));
        toast.success(result?.message || 'IFSC validated');
        // Move focus to account holder name field after successful validation
        setTimeout(() => {
          const accountHolderInput = document.getElementById('accountHolderName');
          if (accountHolderInput) {
            accountHolderInput.focus();
          }
        }, 100);
      } else {
        toast.error(result?.message || 'Failed to validate IFSC');
      }
    } catch (error) {
      console.error('Error validating IFSC:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIfscValidating(false);
    }
  };

  // Helper function to format date from YYYY-MM-DD to DD-MM-YYYY
  const formatDateToDDMMYYYY = (dateString) => {
    if (!dateString) return '';
    // If already in DD-MM-YYYY format, return as is
    if (dateString.includes('-') && dateString.split('-')[0].length === 2) {
      return dateString;
    }
    // Convert from YYYY-MM-DD to DD-MM-YYYY
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString; // Return original if invalid
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Helper function to prepare addresses for API
  const prepareAddresses = () => {
    const addresses = [];

    formData.addresses.forEach((address) => {
      const addressData = {
        addressType: address.addressType,
        address1: address.address1,
        address2: address.address2 || '',
        country: address.country || 'India',
        state: address.state,
        city: address.city,
        pincode: address.pincode
      };
      addresses.push(addressData);
    });

    return addresses;
  };

  // Helper function to prepare experiences for API
  const prepareExperiences = () => {
    return formData.experiences.map((exp) => ({
      companyName: exp.companyName || '',
      designation: exp.designation || '',
      periodFrom: exp.periodFrom ? formatDateToDDMMYYYY(exp.periodFrom) : '',
      periodTo: exp.isPresent ? '' : (exp.periodTo ? formatDateToDDMMYYYY(exp.periodTo) : ''),
      isPresent: exp.isPresent || false,
      duration: exp.duration || ''
    }));
  };

  // Helper function to prepare education details for API
  const prepareEducationDetails = () => {
    return formData.educationDetails.map((edu) => ({
      qualification: edu.qualification || '',
      boardUniversity: edu.boardUniversity || '',
      yearOfPassing: edu.yearOfPassing || '',
      percentage: edu.percentage || '',
      relevantCertificateNo: edu.relevantCertificateNo || '',
      fieldOfStudy: edu.fieldOfStudy || '',
      nameOfDegree: edu.nameOfDegree || ''
    }));
  };

  // Helper function to get presigned URLs from backend
  const getPresignedUrls = async (fileNames, contentTypes) => {
    if (!API_URL) {
      throw new Error('API URL is not configured');
    }

    // Use employee name from form data, or generate a temporary name
    let employeeName = '';
    if (formData.firstName && formData.lastName) {
      employeeName = `${formData.firstName}_${formData.lastName}`.toLowerCase().replace(/\s+/g, '_');
    } else {
      // If name not available yet, use a temporary identifier
      employeeName = `temp_${Date.now()}`;
    }
    
    const payload = {
      action: 'get-employee-document-upload-url',
      employeeName: employeeName,
      fileNames: fileNames,
      contentTypes: contentTypes
    };

    const response = await axios.post(API_URL, payload, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (response.data?.success && response.data?.data) {
      return response.data.data;
    } else {
      throw new Error(response.data?.message || 'Failed to get presigned URLs');
    }
  };

  // Helper function to upload file to S3 using presigned URL
  const uploadFileToS3 = async (file, uploadUrl, contentType) => {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': contentType
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to upload file to S3: ${response.statusText}`);
    }

    return true;
  };

  // Helper function to upload a single file and return S3 key
  const uploadFile = async (file, fileType, index = null) => {
    try {
      // Set uploading state
      if (fileType === 'aadharCard') {
        setUploadingFiles(prev => ({ ...prev, aadharCard: true }));
      } else if (fileType === 'panCard') {
        setUploadingFiles(prev => ({ ...prev, panCard: true }));
      } else if (fileType === 'educationCertificate' && index !== null) {
        setUploadingFiles(prev => ({
          ...prev,
          educationCertificates: { ...prev.educationCertificates, [index]: true }
        }));
      }

      // Get file extension
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const contentType = file.type || (fileExtension === 'pdf' ? 'application/pdf' : 'image/jpeg');
      const fileName = file.name;

      // Get presigned URL
      const uploadData = await getPresignedUrls([fileName], [contentType]);
      const fileData = uploadData[0];

      // Upload to S3
      await uploadFileToS3(file, fileData.uploadUrl, fileData.contentType);

      // Return S3 key - prioritize s3Key from API, then extract from fullUrl, then from uploadUrl
      if (fileData.s3Key) {
        return fileData.s3Key;
      }
      // If fullUrl is provided, extract key from it (remove query parameters first)
      if (fileData.fullUrl) {
        // Remove query parameters from fullUrl before extracting S3 key
        const fullUrlWithoutParams = fileData.fullUrl.split('?')[0];
        const urlParts = fullUrlWithoutParams.split('.com/');
        if (urlParts.length > 1) {
          return urlParts[1];
        }
      }
      // Fallback: extract from uploadUrl
      const uploadUrlWithoutParams = fileData.uploadUrl.split('?')[0];
      const urlParts = uploadUrlWithoutParams.split('.com/');
      if (urlParts.length > 1) {
        return urlParts[1];
      }
      // Last resort: return the uploadUrl without params (shouldn't happen)
      throw new Error('Unable to extract S3 key from API response');
    } catch (error) {
      console.error(`Error uploading ${fileType}:`, error);
      toast.error(`Failed to upload ${fileType === 'aadharCard' ? 'Aadhar Card' : fileType === 'panCard' ? 'PAN Card' : 'Education Certificate'}. Please try again.`);
      throw error;
    } finally {
      // Clear uploading state
      if (fileType === 'aadharCard') {
        setUploadingFiles(prev => ({ ...prev, aadharCard: false }));
      } else if (fileType === 'panCard') {
        setUploadingFiles(prev => ({ ...prev, panCard: false }));
      } else if (fileType === 'educationCertificate' && index !== null) {
        setUploadingFiles(prev => ({
          ...prev,
          educationCertificates: { ...prev.educationCertificates, [index]: false }
        }));
      }
    }
  };

  // Helper function to prepare file data for API
  // Expects S3 keys (strings) instead of full URLs
  const prepareFileData = async () => {
    const fileData = {
      aadharCard: null,
      panCard: null,
      educationCertificates: []
    };

    // Aadhar Card - expect S3 key (string)
    if (formData.aadharCard) {
      if (typeof formData.aadharCard === 'string' && formData.aadharCard.trim()) {
        fileData.aadharCard = formData.aadharCard.trim();
      }
    }

    // PAN Card - expect S3 key (string)
    if (formData.panCard) {
      if (typeof formData.panCard === 'string' && formData.panCard.trim()) {
        fileData.panCard = formData.panCard.trim();
      }
    }

    // Education Certificates - expect array of S3 keys (strings)
    if (formData.educationCertificates && formData.educationCertificates.length > 0) {
      fileData.educationCertificates = formData.educationCertificates
        .filter(key => typeof key === 'string' && key.trim())
        .map(key => key.trim());
    }

    return fileData;
  };
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      toast.error('Email is missing. Please login again.');
      return;
    }

    // Validate all required fields
    if (!validateBasicInfo()) {
      toast.error('Please fill all required fields in Basic Info');
      setActiveTab('basic');
      return;
    }

    if (!formData.addresses || formData.addresses.length === 0) {
      toast.error('Please add at least one address');
      setActiveTab('basic');
      return;
    }

    if (!API_URL) {
      toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
      return;
    }

    // Check if any files are still uploading
    if (uploadingFiles.aadharCard || uploadingFiles.panCard || 
        Object.values(uploadingFiles.educationCertificates).some(uploading => uploading)) {
      toast.error('Please wait for file uploads to complete');
      setActiveTab('document');
      return;
    }

    // Check if files are File objects (not yet uploaded) - this shouldn't happen but handle it
    if (formData.aadharCard && formData.aadharCard instanceof File) {
      toast.error('Please wait for Aadhar Card to finish uploading');
      setActiveTab('document');
      return;
    }
    if (formData.panCard && formData.panCard instanceof File) {
      toast.error('Please wait for PAN Card to finish uploading');
      setActiveTab('document');
      return;
    }
    if (formData.educationCertificates.some(cert => cert instanceof File)) {
      toast.error('Please wait for all education certificates to finish uploading');
      setActiveTab('document');
      return;
    }

    // Validate required documents
    if (!formData.aadharCard || (typeof formData.aadharCard === 'string' && !formData.aadharCard.trim())) {
      toast.error('Please upload Aadhar Card');
      setActiveTab('document');
      return;
    }

    if (!formData.panCard || (typeof formData.panCard === 'string' && !formData.panCard.trim())) {
      toast.error('Please upload PAN Card');
      setActiveTab('document');
      return;
    }

    if (!formData.educationCertificates || formData.educationCertificates.length === 0) {
      toast.error('Please upload at least one Education Certificate');
      setActiveTab('document');
      return;
    }

    setLoading(true);
    try {
      // Prepare file data (S3 keys)
      const fileData = await prepareFileData();

      const payload = {
        action: 'accept-employee-invite',
        email: email,
        // Basic Info - Required
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        gender: formData.gender,
        dateOfBirth: formatDateToDDMMYYYY(formData.dateOfBirth),
        citizenship: formData.citizenship,
        mobileNumber: formData.mobileNumber,
        maritalStatus: formData.maritalStatus,
        // Basic Info - Optional
        ...(formData.middleName && { middleName: formData.middleName.trim() }),
        ...(formData.bloodGroup && { bloodGroup: formData.bloodGroup }),
        aadharNumber: formData.aadharNumber.trim(),
        panNumber: formData.panNumber.toUpperCase().trim(),
        ...(formData.countryCode && { countryCode: formData.countryCode }),
        // Addresses - Required
        addresses: prepareAddresses(),
        // Experiences - Optional
        ...(formData.experiences.length > 0 && { experiences: prepareExperiences() }),
        // Education Details - Optional
        ...(formData.educationDetails.length > 0 && { educationDetails: prepareEducationDetails() }),
        // Bank Details - Optional
        ...(formData.bankName && { bankName: formData.bankName.trim() }),
        ...(formData.accountNumber && { accountNumber: formData.accountNumber }),
        ...(formData.ifscCode && { ifscCode: formData.ifscCode.toUpperCase() }),
        ...(formData.branchName && { branchName: formData.branchName.trim() }),
        ...(formData.accountHolderName && { accountHolderName: formData.accountHolderName.trim() }),
        // Documents - Optional (S3 Keys)
        ...(fileData.aadharCard && { aadharCard: fileData.aadharCard }),
        ...(fileData.panCard && { panCard: fileData.panCard }),
        ...(fileData.educationCertificates.length > 0 && { educationCertificates: fileData.educationCertificates })
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        toast.success(result.message || 'Profile completed successfully!');
        setTimeout(() => {
          navigate('/waiting', { state: { fromEmployeeSelf: true } });
        }, 2000);
      } else {
        const errorMessage = result?.message || 'Failed to complete profile. Please try again.';
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error completing profile:', error);
      let errorMessage = 'Something went wrong. Please try again.';

      if (error.response) {
        const responseData = error.response.data;
        errorMessage = responseData?.message || `Error: ${error.response.status} ${error.response.statusText}`;

        if (error.response.status === 409) {
          errorMessage = responseData?.message || 'Email already registered';
        } else if (error.response.status === 400) {
          errorMessage = responseData?.message || 'Invalid data. Please check all fields.';
        } else if (error.response.status === 401 || error.response.status === 403) {
          errorMessage = responseData?.message || 'Invalid or expired invitation link.';
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Validate Basic Info Tab
  const validateBasicInfo = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First Name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last Name is required';
    }
    if (!formData.gender) {
      newErrors.gender = 'Gender is required';
    }
    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of Birth is required';
    }
    if (!formData.citizenship) {
      newErrors.citizenship = 'Citizenship is required';
    }
    if (!formData.mobileNumber || formData.mobileNumber.length !== 10) {
      newErrors.mobileNumber = 'Valid 10-digit mobile number is required';
    }
    if (!formData.maritalStatus) {
      newErrors.maritalStatus = 'Marital Status is required';
    }
    if (!formData.aadharNumber || formData.aadharNumber.trim().length !== 12) {
      newErrors.aadharNumber = 'Valid 12-digit Aadhar number is required';
    }
    if (!formData.panNumber || formData.panNumber.trim().length !== 10) {
      newErrors.panNumber = 'Valid 10-character PAN number is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate Education Tab
  const validateEducation = () => {
    if (formData.educationDetails.length === 0) {
      toast.error('Please add at least one education record');
      return false;
    }

    for (let i = 0; i < formData.educationDetails.length; i++) {
      const edu = formData.educationDetails[i];
      if (!edu.qualification || !edu.boardUniversity || !edu.yearOfPassing || !edu.percentage || !edu.relevantCertificateNo) {
        toast.error('Please fill all required fields for education record ' + (i + 1));
        return false;
      }
    }

    return true;
  };

  // Validate Bank Tab
  const validateBank = () => {
    const newErrors = {};

    if (!formData.bankName.trim()) {
      newErrors.bankName = 'Bank Name is required';
    }
    if (!formData.accountNumber.trim()) {
      newErrors.accountNumber = 'Account Number is required';
    }
    if (!formData.ifscCode.trim()) {
      newErrors.ifscCode = 'IFSC Code is required';
    }
    if (!formData.accountHolderName.trim()) {
      newErrors.accountHolderName = 'Account Holder Name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check if all required documents are uploaded
  const areRequiredDocumentsUploaded = () => {
    // Check Aadhar Card
    const hasAadharCard = formData.aadharCard && 
      (typeof formData.aadharCard === 'string' ? formData.aadharCard.trim() !== '' : true) &&
      !(formData.aadharCard instanceof File);

    // Check PAN Card
    const hasPanCard = formData.panCard && 
      (typeof formData.panCard === 'string' ? formData.panCard.trim() !== '' : true) &&
      !(formData.panCard instanceof File);

    // Check Education Certificates (at least one)
    const hasEducationCertificates = formData.educationCertificates && 
      formData.educationCertificates.length > 0 &&
      !formData.educationCertificates.some(cert => cert instanceof File);

    // Check if any files are still uploading
    const isUploading = uploadingFiles.aadharCard || 
      uploadingFiles.panCard || 
      Object.values(uploadingFiles.educationCertificates).some(uploading => uploading);

    return hasAadharCard && hasPanCard && hasEducationCertificates && !isUploading;
  };


  // Handle Save and Next for Basic Info
  const handleBasicInfoSaveAndNext = (e) => {
    e.preventDefault();
    if (validateBasicInfo()) {
      // Check if at least one address is added
      if (!formData.addresses || formData.addresses.length === 0) {
        toast.error('Please add at least one address before proceeding');
        return;
      }
      toast.success('Basic info saved successfully!');
      // Mark experience tab as accessible
      setValidatedTabs(prev => {
        if (!prev.includes('experience')) {
          return [...prev, 'experience'];
        }
        return prev;
      });
      setActiveTab('experience');
    } else {
      toast.error('Please fill all required fields');
    }
  };

  // Check if mandatory fields are filled (for address button)
  const areMandatoryFieldsFilled = () => {
    return (
      formData.firstName.trim() !== '' &&
      formData.lastName.trim() !== '' &&
      formData.gender !== '' &&
      formData.dateOfBirth !== '' &&
      formData.citizenship !== '' &&
      formData.email.trim() !== '' &&
      formData.mobileNumber.length === 10 &&
      formData.maritalStatus !== '' &&
      formData.aadharNumber && formData.aadharNumber.trim().length === 12 &&
      formData.panNumber && formData.panNumber.trim().length === 10
    );
  };

  const handleAddAddress = () => {
    if (!areMandatoryFieldsFilled()) {
      toast.error('Please fill all mandatory fields before adding address');
      return;
    }
    setShowAddressModal(true);
  };

  const handleCloseAddressModal = () => {
    setShowAddressModal(false);
    setShowStateDropdown(false);
    setStateSearchTerm('');
    setActiveStateDropdown(null);
    setShowCountryDropdown(false);
    setCountrySearchTerm('');
    setActiveCountryDropdown(null);
    setAddressData({
      permanent: {
        address1: '',
        address2: '',
        country: 'India',
        state: 'Tamil Nadu',
        city: '',
        pincode: ''
      },
      present: {
        address1: '',
        address2: '',
        country: 'India',
        state: 'Tamil Nadu',
        city: '',
        pincode: ''
      },
      sameAsPermanent: false
    });
    setAddressErrors({});
  };

  const handleAddressChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'sameAsPermanent') {
      setAddressData(prev => ({
        ...prev,
        sameAsPermanent: checked,
        // If checked, copy permanent address to present address
        ...(checked && {
          present: { ...prev.permanent }
        })
      }));
    } else if (name.startsWith('permanent.')) {
      const field = name.split('.')[1];
      setAddressData(prev => ({
        ...prev,
        permanent: {
          ...prev.permanent,
          [field]: value
        },
        // If sameAsPermanent is checked, update present address too
        ...(prev.sameAsPermanent && {
          present: {
            ...prev.present,
            [field]: value
          }
        })
      }));
    } else if (name.startsWith('present.')) {
      const field = name.split('.')[1];
      setAddressData(prev => ({
        ...prev,
        present: {
          ...prev.present,
          [field]: value
        }
      }));
    } else {
      setAddressData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }

    // Clear error when user starts typing
    const errorKey = name.includes('.') ? name : name;
    if (addressErrors[errorKey] || addressErrors[name]) {
      setAddressErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleStateSelect = (state, addressType) => {
    setAddressData(prev => ({
      ...prev,
      [addressType]: {
        ...prev[addressType],
        state: state
      },
      // If sameAsPermanent is checked and we're updating permanent, update present too
      ...(addressType === 'permanent' && prev.sameAsPermanent && {
        present: {
          ...prev.present,
          state: state
        }
      })
    }));
    setShowStateDropdown(false);
    setStateSearchTerm('');
    setActiveStateDropdown(null);
    if (addressErrors[`${addressType}.state`]) {
      setAddressErrors(prev => ({
        ...prev,
        [`${addressType}.state`]: ''
      }));
    }
  };

  const handleStateKeyDown = (e, addressType) => {
    if (e.key === 'Enter' && filteredStates.length > 0) {
      e.preventDefault();
      handleStateSelect(filteredStates[0], addressType);
    } else if (e.key === 'Escape') {
      setShowStateDropdown(false);
      setStateSearchTerm('');
      setActiveStateDropdown(null);
    } else if (e.key === 'Tab') {
      // Allow default tab behavior
      setShowStateDropdown(false);
      setStateSearchTerm('');
      setActiveStateDropdown(null);
    }
  };


  const handleCountrySelect = (country, addressType) => {
    setAddressData(prev => ({
      ...prev,
      [addressType]: {
        ...prev[addressType],
        country: country
      },
      // If sameAsPermanent is checked and we're updating permanent, update present too
      ...(addressType === 'permanent' && prev.sameAsPermanent && {
        present: {
          ...prev.present,
          country: country
        }
      })
    }));
    setShowCountryDropdown(false);
    setCountrySearchTerm('');
    setActiveCountryDropdown(null);
    if (addressErrors[`${addressType}.country`]) {
      setAddressErrors(prev => ({
        ...prev,
        [`${addressType}.country`]: ''
      }));
    }
  };

  const handleCountryKeyDown = (e, addressType) => {
    if (e.key === 'Enter' && filteredCountries.length > 0) {
      e.preventDefault();
      handleCountrySelect(filteredCountries[0], addressType);
    } else if (e.key === 'Escape') {
      setShowCountryDropdown(false);
      setCountrySearchTerm('');
      setActiveCountryDropdown(null);
    } else if (e.key === 'Tab') {
      // Allow default tab behavior
      setShowCountryDropdown(false);
      setCountrySearchTerm('');
      setActiveCountryDropdown(null);
    }
  };

  const filteredStates = indianStates.filter(state =>
    state.toLowerCase().includes(stateSearchTerm.toLowerCase())
  );

  const filteredCountries = countries.filter(country =>
    country.toLowerCase().includes(countrySearchTerm.toLowerCase())
  );

  const handlePincodeChange = (e) => {
    const value = e.target.value;
    const name = e.target.name; // Should be 'permanent.pincode' or 'present.pincode'
    // Only allow numeric characters and limit to 6 digits
    const numericValue = value.replace(/\D/g, '').slice(0, 6);

    if (name.startsWith('permanent.')) {
      setAddressData(prev => ({
        ...prev,
        permanent: {
          ...prev.permanent,
          pincode: numericValue
        },
        // If sameAsPermanent is checked, update present too
        ...(prev.sameAsPermanent && {
          present: {
            ...prev.present,
            pincode: numericValue
          }
        })
      }));
    } else if (name.startsWith('present.')) {
      setAddressData(prev => ({
        ...prev,
        present: {
          ...prev.present,
          pincode: numericValue
        }
      }));
    }

    const errorKey = name.includes('.') ? name : name;
    if (addressErrors[errorKey]) {
      setAddressErrors(prev => ({
        ...prev,
        [errorKey]: ''
      }));
    }
  };

  const handleSaveAddress = (e) => {
    e.preventDefault();

    // Validation for permanent address
    const newErrors = {};
    if (!addressData.permanent.address1.trim()) {
      newErrors['permanent.address1'] = 'Permanent Address 1 is required';
    }
    if (!addressData.permanent.country) {
      newErrors['permanent.country'] = 'Permanent Country is required';
    }
    if (!addressData.permanent.state) {
      newErrors['permanent.state'] = 'Permanent State is required';
    }
    if (!addressData.permanent.city.trim()) {
      newErrors['permanent.city'] = 'Permanent City is required';
    }
    if (!addressData.permanent.pincode || addressData.permanent.pincode.length !== 6) {
      newErrors['permanent.pincode'] = 'Valid 6-digit pincode is required for permanent address';
    }

    // Validation for present address (only if not same as permanent)
    if (!addressData.sameAsPermanent) {
      if (!addressData.present.address1.trim()) {
        newErrors['present.address1'] = 'Present Address 1 is required';
      }
      if (!addressData.present.country) {
        newErrors['present.country'] = 'Present Country is required';
      }
      if (!addressData.present.state) {
        newErrors['present.state'] = 'Present State is required';
      }
      if (!addressData.present.city.trim()) {
        newErrors['present.city'] = 'Present City is required';
      }
      if (!addressData.present.pincode || addressData.present.pincode.length !== 6) {
        newErrors['present.pincode'] = 'Valid 6-digit pincode is required for present address';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setAddressErrors(newErrors);
      toast.error('Please fill all required fields');
      return false; // Return false if validation fails
    }

    // Prepare permanent address
    const permanentAddress = {
      address1: addressData.permanent.address1,
      address2: addressData.permanent.address2 || '',
      country: addressData.permanent.country,
      state: addressData.permanent.state,
      city: addressData.permanent.city,
      pincode: addressData.permanent.pincode,
      addressType: 'permanent',
      id: Date.now(),
      updatedAt: Date.now()
    };

    // Prepare present address
    const presentAddress = {
      address1: addressData.sameAsPermanent ? addressData.permanent.address1 : addressData.present.address1,
      address2: addressData.sameAsPermanent ? addressData.permanent.address2 : (addressData.present.address2 || ''),
      country: addressData.sameAsPermanent ? addressData.permanent.country : addressData.present.country,
      state: addressData.sameAsPermanent ? addressData.permanent.state : addressData.present.state,
      city: addressData.sameAsPermanent ? addressData.permanent.city : addressData.present.city,
      pincode: addressData.sameAsPermanent ? addressData.permanent.pincode : addressData.present.pincode,
      addressType: 'present',
      id: Date.now() + 1,
      updatedAt: Date.now()
    };

    // Replace addresses with only permanent and present addresses
    setFormData(prev => ({
      ...prev,
      addresses: [permanentAddress, presentAddress]
    }));

    toast.success('Permanent and Present addresses added successfully!');
    handleCloseAddressModal();
    return true; // Return true after successful save
  };

  const handleSaveAddressButton = (e) => {
    e.preventDefault();
    handleSaveAddress(e);
  };



  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleAddExperience = () => {
    const newExperience = {
      id: Date.now(),
      companyName: '',
      designation: '',
      periodFrom: '',
      periodTo: '',
      isPresent: false,
      duration: ''
    };
    setFormData(prev => ({
      ...prev,
      experiences: [...prev.experiences, newExperience]
    }));
  };

  const handleExperienceChange = (index, field, value) => {
    // Validate date fields - ensure year is 4 digits (same as Holiday.js)
    if ((field === 'periodFrom' || field === 'periodTo') && value) {
      const parts = value.split('-');
      if (parts.length === 3 && parts[0].length !== 4) {
        // Year must be 4 digits, don't update state
        return;
      }
    }

    setFormData(prev => {
      const updatedExperiences = [...prev.experiences];
      updatedExperiences[index] = {
        ...updatedExperiences[index],
        [field]: value
      };

      // Calculate duration if period dates changed
      if (field === 'periodFrom' || field === 'periodTo' || field === 'isPresent') {
        const experience = updatedExperiences[index];
        if (experience.periodFrom) {
          const fromDate = new Date(experience.periodFrom);
          if (!isNaN(fromDate.getTime())) {
            let toDate;
            if (experience.isPresent) {
              toDate = new Date();
            } else if (experience.periodTo) {
              toDate = new Date(experience.periodTo);
              if (isNaN(toDate.getTime())) {
                updatedExperiences[index].duration = '';
                return {
                  ...prev,
                  experiences: updatedExperiences
                };
              }
            } else {
              updatedExperiences[index].duration = '';
              return {
                ...prev,
                experiences: updatedExperiences
              };
            }

            // Calculate difference
            const fromYear = fromDate.getFullYear();
            const fromMonth = fromDate.getMonth();
            const toYear = toDate.getFullYear();
            const toMonth = toDate.getMonth();

            let years = toYear - fromYear;
            let months = toMonth - fromMonth;

            // Adjust if end day is before start day
            if (months < 0) {
              years--;
              months += 12;
            }

            if (toDate.getDate() < fromDate.getDate()) {
              months--;
              if (months < 0) {
                years--;
                months += 12;
              }
            }

            let duration = '';
            if (years === 0 && months === 0) {
              const diffTime = toDate - fromDate;
              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
              duration = `${diffDays} day${diffDays > 1 ? 's' : ''}`;
            } else if (years === 0) {
              duration = `${months} month${months > 1 ? 's' : ''}`;
            } else if (months === 0) {
              duration = `${years} year${years > 1 ? 's' : ''}`;
            } else {
              duration = `${years} year${years > 1 ? 's' : ''} ${months} month${months > 1 ? 's' : ''}`;
            }

            updatedExperiences[index].duration = duration;
          } else {
            updatedExperiences[index].duration = '';
          }
        } else {
          updatedExperiences[index].duration = '';
        }
      }

      return {
        ...prev,
        experiences: updatedExperiences
      };
    });
  };

  const handleRemoveExperience = (id) => {
    setFormData(prev => ({
      ...prev,
      experiences: prev.experiences.filter(exp => exp.id !== id)
    }));
  };

  const getPermanentAddress = () => {
    return formData.addresses?.find(addr => addr.addressType === 'permanent');
  };

  const getPresentAddress = () => {
    return formData.addresses?.find(addr => addr.addressType === 'present');
  };

  const handleAddEducation = () => {
    const newEducation = {
      id: Date.now(),
      qualification: '',
      boardUniversity: '',
      yearOfPassing: '',
      percentage: '',
      relevantCertificateNo: '',
      fieldOfStudy: '',
      nameOfDegree: ''
    };
    setFormData(prev => ({
      ...prev,
      educationDetails: [...prev.educationDetails, newEducation]
    }));
  };

  const handleEducationChange = (index, field, value) => {
    let validatedValue = value;

    // Validate Year of Passing - only 4 digits, no alphabets
    if (field === 'yearOfPassing') {
      // Only allow numeric characters, max 4 digits
      validatedValue = value.replace(/\D/g, '').slice(0, 4);
    }

    // Validate Percentage/CGPA - only numeric with decimal point, no alphabets
    if (field === 'percentage') {
      // Allow numbers and one decimal point
      validatedValue = value.replace(/[^\d.]/g, '');
      // Ensure only one decimal point
      const parts = validatedValue.split('.');
      if (parts.length > 2) {
        validatedValue = parts[0] + '.' + parts.slice(1).join('');
      }
    }

    setFormData(prev => {
      const updatedEducation = [...prev.educationDetails];
      const currentEducation = updatedEducation[index];

      // If qualification is changed, clear fieldOfStudy and nameOfDegree if not applicable
      if (field === 'qualification') {
        updatedEducation[index] = {
          ...currentEducation,
          [field]: validatedValue,
          // Clear fieldOfStudy if 10th is selected
          ...(validatedValue === '10th' && { fieldOfStudy: '' }),
          // Clear nameOfDegree if 10th or 12th is selected
          ...((validatedValue === '10th' || validatedValue === '12th') && { nameOfDegree: '' })
        };
      } else {
        updatedEducation[index] = {
          ...currentEducation,
          [field]: validatedValue
        };
      }

      return {
        ...prev,
        educationDetails: updatedEducation
      };
    });
  };

  const handleRemoveEducation = (id) => {
    setFormData(prev => ({
      ...prev,
      educationDetails: prev.educationDetails.filter(edu => edu.id !== id)
    }));
  };

  return (
    <div className="employee-form">
      <section className="content">
        <div className="container-fluid">
          {/* Header with Title and Tabs */}
          <div className="row mb-3 employee-header-row">
            {/* Left Side - Employee Title and Description */}
            <div className="col-md-5">
              <h1 className="employee-title">
                Complete Your Profile
                <i className="fas fa-info-circle ml-2 employee-title-icon"></i>
              </h1>
              <p className="employee-description">
                Please complete your profile information to continue.
              </p>
            </div>

            {/* Right Side - Tabs Navigation */}
            <div className="col-md-7 tabs-container">
              <ul className="nav nav-tabs employee-tabs-list" id="employee-tabs" role="tablist">
                {tabs.map((tab) => {
                  const isAccessible = validatedTabs.includes(tab.id);
                  const tabIndex = tabs.findIndex(t => t.id === tab.id);

                  return (
                    <li className="nav-item" key={tab.id}>
                      <div
                        className={`nav-link employee-tab-link ${activeTab === tab.id ? 'active' : ''} ${!isAccessible ? 'disabled' : ''}`}
                        onClick={() => {
                          if (isAccessible) {
                            setActiveTab(tab.id);
                          } else {
                            toast.error(`Please complete ${tabs[tabIndex - 1]?.label || 'previous'} tab first`);
                          }
                        }}
                      >
                        {tab.label}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          {/* Tabs Content Card */}
          <div className="card employee-content-card">
            <div className="card-body">
              {/* Basic Info Tab */}
              {activeTab === 'basic' && (
                <form onSubmit={handleSubmit}>
                  {/* Basic Info Section */}
                  <div className="mb-4">
                    <h4 className="section-title">Basic Info</h4>
                    <p className="section-subtitle">
                      Fill your profile details below
                    </p>

                    <div className="form-row-3">
                      <div className="form-group">
                        <label htmlFor="firstName" className="form-label">
                          First Name<span className="required-star">*</span>
                        </label>
                        <input
                          type="text"
                          id="firstName"
                          name="firstName"
                          className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                          value={formData.firstName}
                          onChange={handleChange}
                          placeholder="Enter first name"
                        />
                        {errors.firstName && (
                          <div className="invalid-feedback">{errors.firstName}</div>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor="middleName" className="form-label">Middle Name</label>
                        <input
                          type="text"
                          id="middleName"
                          name="middleName"
                          className="form-control"
                          value={formData.middleName}
                          onChange={handleChange}
                          placeholder="Enter middle name"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="lastName" className="form-label">
                          Last Name<span className="required-star">*</span>
                        </label>
                        <input
                          type="text"
                          id="lastName"
                          name="lastName"
                          className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                          value={formData.lastName}
                          onChange={handleChange}
                          placeholder="Enter last name"
                        />
                        {errors.lastName && (
                          <div className="invalid-feedback">{errors.lastName}</div>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor="dateOfBirth" className="form-label">
                          Date Of Birth<span className="required-star">*</span>
                        </label>
                        <div className="date-input-wrapper">
                          <input
                            type="date"
                            id="dateOfBirth"
                            name="dateOfBirth"
                            className={`form-control ${errors.dateOfBirth ? 'is-invalid' : ''}`}
                            value={formData.dateOfBirth}
                            onChange={handleChange}
                          />
                          <i className="fas fa-calendar-alt date-icon"></i>
                        </div>
                        {errors.dateOfBirth && (
                          <div className="invalid-feedback">{errors.dateOfBirth}</div>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor="gender" className="form-label">
                          Gender<span className="required-star">*</span>
                        </label>
                        <select
                          id="gender"
                          name="gender"
                          className={`form-control ${errors.gender ? 'is-invalid' : ''}`}
                          value={formData.gender}
                          onChange={handleChange}
                        >
                          <option value="">Select Gender</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                        </select>
                        {errors.gender && (
                          <div className="invalid-feedback">{errors.gender}</div>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor="bloodGroup" className="form-label">Blood Group</label>
                        <select
                          id="bloodGroup"
                          name="bloodGroup"
                          className="form-control"
                          value={formData.bloodGroup}
                          onChange={handleChange}
                        >
                          <option value="">Select Your Blood Group</option>
                          <option value="A+">A+</option>
                          <option value="A-">A-</option>
                          <option value="B+">B+</option>
                          <option value="B-">B-</option>
                          <option value="AB+">AB+</option>
                          <option value="AB-">AB-</option>
                          <option value="O+">O+</option>
                          <option value="O-">O-</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label htmlFor="citizenship" className="form-label">
                          Citizenship<span className="required-star">*</span>
                        </label>
                        <select
                          id="citizenship"
                          name="citizenship"
                          className={`form-control ${errors.citizenship ? 'is-invalid' : ''}`}
                          value={formData.citizenship}
                          onChange={handleChange}
                        >
                          <option value="">Select Citizenship</option>
                          <option value="indian">Indian</option>
                          <option value="other">Other</option>
                        </select>
                        {errors.citizenship && (
                          <div className="invalid-feedback">{errors.citizenship}</div>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor="email" className="form-label">
                          Email<span className="required-star">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          className="form-control"
                          value={formData.email}
                          disabled
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="mobileNumber" className="form-label">
                          Mobile Number<span className="required-star">*</span>
                        </label>
                        <div className="mobile-input-group">
                          <span className="country-code-prefix">+91</span>
                          <input
                            type="tel"
                            id="mobileNumber"
                            name="mobileNumber"
                            className={`form-control mobile-input ${errors.mobileNumber ? 'is-invalid' : ''}`}
                            value={formData.mobileNumber}
                            onChange={handleMobileNumberChange}
                            placeholder="Enter 10 digit mobile number"
                            maxLength="10"
                            inputMode="numeric"
                          />
                        </div>
                        {errors.mobileNumber && (
                          <div className="invalid-feedback">{errors.mobileNumber}</div>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor="maritalStatus" className="form-label">
                          Marital Status<span className="required-star">*</span>
                        </label>
                        <select
                          id="maritalStatus"
                          name="maritalStatus"
                          className={`form-control ${errors.maritalStatus ? 'is-invalid' : ''}`}
                          value={formData.maritalStatus}
                          onChange={handleChange}
                        >
                          <option value="">Select Marital Status</option>
                          <option value="Single">Single</option>
                          <option value="married">Married</option>
                        </select>
                        {errors.maritalStatus && (
                          <div className="invalid-feedback">{errors.maritalStatus}</div>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor="aadharNumber" className="form-label">Aadhar Number <span className="text-danger">*</span></label>
                        <input
                          type="tel"
                          id="aadharNumber"
                          inputMode="numeric"
                          name="aadharNumber"
                          className={`form-control ${errors.aadharNumber ? 'is-invalid' : ''}`}
                          value={formData.aadharNumber}
                          onChange={handleChange}
                          placeholder="Enter Aadhar number"
                          maxLength="12"
                          required
                        />
                        {errors.aadharNumber && (
                          <div className="invalid-feedback">{errors.aadharNumber}</div>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor="panNumber" className="form-label">PAN Number <span className="text-danger">*</span></label>
                        <input
                          type="text"
                          id="panNumber"
                          name="panNumber"
                          className={`form-control text-uppercase ${errors.panNumber ? 'is-invalid' : ''}`}
                          value={formData.panNumber}
                          onChange={handleChange}
                          placeholder="Enter Pan number"
                          maxLength="10"
                          required
                        />
                        {errors.panNumber && (
                          <div className="invalid-feedback">{errors.panNumber}</div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Address Section */}
                  <div className="section-divider">
                    <div className="section-header">
                      <div className="section-header-content">
                        <h4>Address</h4>
                        <p>Your permanent & present address</p>
                      </div>
                      <button
                        type="button"
                        onClick={handleAddAddress}
                        className={`btn-add ${areMandatoryFieldsFilled() ? 'btn-enabled' : 'btn-disabled'}`}
                        disabled={!areMandatoryFieldsFilled()}
                      >
                        Add
                      </button>
                    </div>

                    {/* Display Saved Addresses - Side by Side */}
                    {(getPermanentAddress() || getPresentAddress()) && (
                      <div className="address-list address-list-spacing">
                        {/* Permanent Address Card */}
                        <div className="address-card">
                          <div className="address-card-header">
                            <h5 className="address-card-title">
                              Permanent Address
                            </h5>
                          </div>
                          {getPermanentAddress() ? (
                            <>
                              <div className="address-content">
                                <p>{getPermanentAddress().address1}</p>
                                {getPermanentAddress().address2 && <p>{getPermanentAddress().address2}</p>}
                                <p>{getPermanentAddress().city}, {getPermanentAddress().state}</p>
                                <p>{getPermanentAddress().country} - {getPermanentAddress().pincode}</p>
                              </div>
                              <p className="address-update-text">
                                Last update on {formatDate(getPermanentAddress().updatedAt || getPermanentAddress().id)}
                              </p>
                            </>
                          ) : (
                            <div className="address-empty-state">
                              No permanent address
                            </div>
                          )}
                        </div>

                        {/* Present Address Card */}
                        <div className="address-card">
                          <div className="address-card-header">
                            <h5 className="address-card-title">
                              Present Address
                            </h5>
                          </div>
                          {getPresentAddress() ? (
                            <>
                              <div className="address-content">
                                <p>{getPresentAddress().address1}</p>
                                {getPresentAddress().address2 && <p>{getPresentAddress().address2}</p>}
                                <p>{getPresentAddress().city}, {getPresentAddress().state}</p>
                                <p>{getPresentAddress().country} - {getPresentAddress().pincode}</p>
                              </div>
                              <p className="address-update-text">
                                Last update on {formatDate(getPresentAddress().updatedAt || getPresentAddress().id)}
                              </p>
                            </>
                          ) : (
                            <div className="address-empty-state">
                              No present address
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {(!getPermanentAddress() && !getPresentAddress()) && (
                      <div className="address-empty-container">
                        <i className="fas fa-map-marker-alt address-empty-icon"></i>
                        No addresses added yet. Click "Add" to add an address.
                      </div>
                    )}
                  </div>

                  {/* Submit Button */}
                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => navigate('/')}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className={`btn-primary ${(!formData.addresses || formData.addresses.length === 0) ? 'btn-disabled' : 'btn-enabled'}`}
                      onClick={handleBasicInfoSaveAndNext}
                      disabled={!formData.addresses || formData.addresses.length === 0}
                    >
                      Save and Next
                    </button>
                  </div>
                </form>
              )}

              {/* Experience Tab */}
              {activeTab === 'experience' && (
                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <h4 className="section-title">
                      Previous Company Details
                    </h4>
                    <p className="section-subtitle">
                      Add previous work experience (Optional - Skip if fresher)
                    </p>

                    {/* Experience List */}
                    {formData.experiences.map((exp, index) => (
                      <div key={exp.id} className="experience-item experience-item-spacing">
                        <div className="form-row-3">
                          <div className="form-group">
                            <label className="form-label">Company Name</label>
                            <input
                              type="text"
                              className="form-control"
                              value={exp.companyName}
                              onChange={(e) => handleExperienceChange(index, 'companyName', e.target.value)}
                              placeholder="Enter company name"
                            />
                          </div>

                          <div className="form-group">
                            <label className="form-label">Designation</label>
                            <input
                              type="text"
                              className="form-control"
                              value={exp.designation}
                              onChange={(e) => handleExperienceChange(index, 'designation', e.target.value)}
                              placeholder="Enter designation"
                            />
                          </div>

                          <div className="form-group">
                            <label className="form-label">Period From</label>
                            <div className="date-input-wrapper">
                              <input
                                type="date"
                                className="form-control"
                                value={exp.periodFrom}
                                onChange={(e) => {
                                  handleExperienceChange(index, 'periodFrom', e.target.value);
                                }}
                              />
                              <i className="fas fa-calendar-alt date-icon"></i>
                            </div>
                          </div>

                          <div className="form-group">
                            <label className="form-label">Period To</label>
                            {index === 0 ? (
                              <div className="period-to-container">
                                <div className="date-input-wrapper date-wrapper-flex">
                                  <input
                                    type="date"
                                    className={`form-control ${exp.isPresent ? 'date-input-disabled' : 'date-input-enabled'}`}
                                    value={exp.periodTo}
                                    onChange={(e) => {
                                      handleExperienceChange(index, 'periodTo', e.target.value);
                                      handleExperienceChange(index, 'isPresent', false);
                                    }}
                                    disabled={exp.isPresent}
                                  />
                                  <i className="fas fa-calendar-alt date-icon"></i>
                                </div>
                               
                              </div>
                            ) : (
                              <div className="date-input-wrapper">
                                <input
                                  type="date"
                                  className="form-control"
                                  value={exp.periodTo}
                                  onChange={(e) => {
                                    handleExperienceChange(index, 'periodTo', e.target.value);
                                    handleExperienceChange(index, 'isPresent', false);
                                  }}
                                  required
                                />
                                <i className="fas fa-calendar-alt date-icon"></i>
                              </div>
                            )}
                          </div>

                          <div className="form-group">
                            <label className="form-label">Experience Duration</label>
                            <input
                              type="text"
                              className="form-control"
                              value={exp.duration}
                              readOnly
                              placeholder="Auto calculated"
                            />
                          </div>

                          <div className="form-group form-group-flex">
                            <button
                              type="button"
                              onClick={() => handleRemoveExperience(exp.id)}
                              className="btn-remove"
                            >
                              <i className="fas fa-trash-alt"></i> Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Add Experience Button */}
                    <div className="add-item-container">
                      <button
                        type="button"
                        onClick={handleAddExperience}
                        className="btn-add-item"
                      >
                        <i className="fas fa-plus"></i> Add Experience
                      </button>
                    </div>

                    {/* Empty State Message */}
                    {formData.experiences.length === 0 && (
                      <div className="experience-empty-state">
                        <i className="fas fa-briefcase experience-empty-icon"></i>
                        <p className="experience-empty-text">
                          No experience added yet. Click "Add Experience" to add previous work experience, or click "Skip" to proceed as fresher.
                        </p>
                      </div>
                    )}

                    {/* Submit Button */}
                    <div className="form-actions">
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => setActiveTab('basic')}
                      >
                        Previous
                      </button>
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={(e) => {
                          e.preventDefault();
                          toast.success('Experience section skipped. You can proceed as fresher.');
                          // Mark education tab as accessible
                          setValidatedTabs(prev => {
                            if (!prev.includes('education')) {
                              return [...prev, 'education'];
                            }
                            return prev;
                          });
                          setActiveTab('education');
                        }}
                        style={{ marginRight: '10px' }}
                      >
                        Skip
                      </button>
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={(e) => {
                          e.preventDefault();
                          // Validate experiences only if they exist
                          if (formData.experiences.length > 0) {
                            // Validate each experience has required fields
                            const hasInvalidExperience = formData.experiences.some(exp => 
                              !exp.companyName || !exp.designation || !exp.periodFrom || (!exp.isPresent && !exp.periodTo)
                            );
                            
                            if (hasInvalidExperience) {
                              toast.error('Please fill all required fields for experience records');
                              return;
                            }
                            toast.success('Experience details saved successfully!');
                          } else {
                            toast.success('Proceeding without experience (Fresher)');
                          }
                          // Mark education tab as accessible
                          setValidatedTabs(prev => {
                            if (!prev.includes('education')) {
                              return [...prev, 'education'];
                            }
                            return prev;
                          });
                          setActiveTab('education');
                        }}
                      >
                        Save and Next
                      </button>
                    </div>
                  </div>
                </form>
              )}

              {/* Education Tab */}
              {activeTab === 'education' && (
                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <h4 className="section-title">Education Details</h4>
                    <p className="section-subtitle">
                      Add your educational qualifications
                    </p>

                    {/* Education List */}
                    {formData.educationDetails.map((edu, index) => (
                      <div key={edu.id} className="experience-item experience-item-spacing">
                        <div className="form-row-3">
                          <div className="form-group">
                            <label className="form-label">
                              Qualification<span className="required-star">*</span>
                            </label>
                            <select
                              className="form-control"
                              value={edu.qualification}
                              onChange={(e) => handleEducationChange(index, 'qualification', e.target.value)}
                            >
                              <option value="">Select Qualification</option>
                              <option value="10th">10th</option>
                              <option value="12th">12th</option>
                              <option value="Diploma">Diploma</option>
                              <option value="Bachelor's Degree">Bachelor's Degree</option>
                              <option value="Master's Degree">Master's Degree</option>
                              <option value="PhD">PhD</option>
                              <option value="Others">Others</option>
                            </select>
                          </div>

                          <div className="form-group">
                            <label className="form-label">
                              Board/University<span className="required-star">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={edu.boardUniversity}
                              onChange={(e) => handleEducationChange(index, 'boardUniversity', e.target.value)}
                              placeholder="Enter board/university name"
                            />
                          </div>

                          <div className="form-group">
                            <label className="form-label">
                              Year of Passing<span className="required-star">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={edu.yearOfPassing}
                              onChange={(e) => handleEducationChange(index, 'yearOfPassing', e.target.value)}
                              placeholder="YYYY"
                              maxLength="4"
                            />
                          </div>

                          <div className="form-group">
                            <label className="form-label">
                              Percentage/CGPA<span className="required-star">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={edu.percentage}
                              onChange={(e) => handleEducationChange(index, 'percentage', e.target.value)}
                              placeholder="Enter percentage or CGPA"
                            />
                          </div>

                          {/* Name of the Degree - Show for Diploma, Bachelor's, Master's, PhD (not for 10th and 12th) */}
                          {edu.qualification !== '10th' && edu.qualification !== '12th' && (
                            <div className="form-group">
                              <label className="form-label">Name of the Degree</label>
                              <input
                                type="text"
                                className="form-control"
                                value={edu.nameOfDegree}
                                onChange={(e) => handleEducationChange(index, 'nameOfDegree', e.target.value)}
                                placeholder="Enter degree name (e.g., B.Tech, M.Sc)"
                              />
                            </div>
                          )}

                          {/* Field of Study - Show for 12th and above (not for 10th) */}
                          {edu.qualification !== '10th' && (
                            <div className="form-group">
                              <label className="form-label">Field of Study</label>
                              <input
                                type="text"
                                className="form-control"
                                value={edu.fieldOfStudy}
                                onChange={(e) => handleEducationChange(index, 'fieldOfStudy', e.target.value)}
                                placeholder="Enter field of study (e.g., Computer Science)"
                              />
                            </div>
                          )}

                          <div className="form-group">
                            <label className="form-label">
                              Relevant Certificate No<span className="required-star">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={edu.relevantCertificateNo}
                              onChange={(e) => handleEducationChange(index, 'relevantCertificateNo', e.target.value)}
                              placeholder="Enter certificate number"
                            />
                          </div>

                          <div className="form-group form-group-flex">
                            <button
                              type="button"
                              onClick={() => handleRemoveEducation(edu.id)}
                              className="btn-remove"
                            >
                              <i className="fas fa-trash-alt"></i> Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Add Education Button */}
                    <div className="add-item-container">
                      <button
                        type="button"
                        onClick={handleAddEducation}
                        className="btn-add-item"
                      >
                        <i className="fas fa-plus"></i> Add Education
                      </button>
                    </div>

                    {/* Submit Button */}
                    <div className="form-actions">
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => setActiveTab('experience')}
                      >
                        Previous
                      </button>
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={(e) => {
                          e.preventDefault();
                          if (validateEducation()) {
                            toast.success('Education details saved successfully!');
                            // Mark bank tab as accessible
                            setValidatedTabs(prev => {
                              if (!prev.includes('bank')) {
                                return [...prev, 'bank'];
                              }
                              return prev;
                            });
                            setActiveTab('bank');
                          }
                        }}
                      >
                        Save and Next
                      </button>
                    </div>
                  </div>
                </form>
              )}

              {/* Bank Tab */}
              {activeTab === 'bank' && (
                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <h4 className="section-title">Bank Details</h4>
                    <p className="section-subtitle">
                      Add your bank account information
                    </p>

                    <div className="form-row-3">
                      <div className="form-group">
                        <label htmlFor="bankName" className="form-label">
                          Bank Name<span className="required-star">*</span>
                        </label>
                        <input
                          type="text"
                          id="bankName"
                          name="bankName"
                          className={`form-control ${errors.bankName ? 'is-invalid' : ''}`}
                          value={formData.bankName}
                          onChange={handleChange}
                          placeholder="Enter bank name"
                        />
                        {errors.bankName && (
                          <div className="invalid-feedback">{errors.bankName}</div>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor="accountNumber" className="form-label">
                          Account Number<span className="required-star">*</span>
                        </label>
                        <input
                          type="text"
                          id="accountNumber"
                          name="accountNumber"
                          className={`form-control ${errors.accountNumber ? 'is-invalid' : ''}`}
                          value={formData.accountNumber}
                          onChange={handleChange}
                          placeholder="Enter account number"
                        />
                        {errors.accountNumber && (
                          <div className="invalid-feedback">{errors.accountNumber}</div>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor="ifscCode" className="form-label">
                          IFSC Code<span className="required-star">*</span>
                        </label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <input
                            type="text"
                            id="ifscCode"
                            name="ifscCode"
                            className={`form-control text-uppercase ${errors.ifscCode ? 'is-invalid' : ''}`}
                            value={formData.ifscCode}
                            onChange={handleChange}
                            placeholder="Enter IFSC code"
                            style={{ flex: 1 }}
                          />
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => validateIfscCode(formData.ifscCode)}
                            disabled={ifscValidating || !formData.ifscCode.trim()}
                            style={{ whiteSpace: 'nowrap', minWidth: '100px' }}
                          >
                            {ifscValidating ? (
                              <>
                                <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                                Validating...
                              </>
                            ) : (
                              'Validate'
                            )}
                          </button>
                        </div>
                        <small className="form-text text-muted">
                          Click Validate to auto-fill branch name
                        </small>
                        {errors.ifscCode && (
                          <div className="invalid-feedback">{errors.ifscCode}</div>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor="branchName" className="form-label">
                          Branch Name
                        </label>
                        <input
                          type="text"
                          id="branchName"
                          name="branchName"
                          className="form-control"
                          value={formData.branchName}
                          readOnly
                          style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                          placeholder="Branch name will be auto-filled after IFSC validation"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="accountHolderName" className="form-label">
                          Account Holder Name<span className="required-star">*</span>
                        </label>
                        <input
                          type="text"
                          id="accountHolderName"
                          name="accountHolderName"
                          className={`form-control ${errors.accountHolderName ? 'is-invalid' : ''}`}
                          value={formData.accountHolderName}
                          onChange={handleChange}
                          placeholder="Enter account holder name"
                        />
                        {errors.accountHolderName && (
                          <div className="invalid-feedback">{errors.accountHolderName}</div>
                        )}
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="form-actions">
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => setActiveTab('education')}
                      >
                        Previous
                      </button>
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={(e) => {
                          e.preventDefault();
                          if (validateBank()) {
                            toast.success('Bank details saved successfully!');
                            // Mark document tab as accessible
                            setValidatedTabs(prev => {
                              if (!prev.includes('document')) {
                                return [...prev, 'document'];
                              }
                              return prev;
                            });
                            setActiveTab('document');
                          } else {
                            toast.error('Please fill all required fields');
                          }
                        }}
                      >
                        Save and Next
                      </button>
                    </div>
                  </div>
                </form>
              )}

              {/* Document Tab */}
              {activeTab === 'document' && (
                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <h4 className="section-title">Document Details</h4>
                    <p className="section-subtitle">
                      Upload your required documents (All documents are mandatory)
                    </p>

                    <div className="form-row-3">
                      <div className="form-group">
                        <label htmlFor="aadharCard" className="form-label">
                          Aadhar Card<span className="required-star">*</span>
                        </label>
                        <div className="file-input-wrapper">
                          <input
                            type="file"
                            id="aadharCard"
                            name="aadharCard"
                            className="form-control file-input-padding"
                            accept="image/*,.pdf"
                            disabled={uploadingFiles.aadharCard || uploadingFiles.panCard || Object.values(uploadingFiles.educationCertificates).some(uploading => uploading)}
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (file) {
                                try {
                                  // Upload file immediately and store S3 key
                                  const s3Key = await uploadFile(file, 'aadharCard');
                                  setFormData(prev => ({ ...prev, aadharCard: s3Key }));
                                  toast.success('Aadhar Card uploaded successfully');
                                } catch (error) {
                                  // Reset file input on error
                                  e.target.value = '';
                                  setFormData(prev => ({ ...prev, aadharCard: null }));
                                }
                              }
                            }}
                          />
                          {uploadingFiles.aadharCard && (
                            <span className="file-uploading-text">
                              <i className="fas fa-spinner fa-spin"></i> Uploading Aadhar Card...
                            </span>
                          )}
                          {formData.aadharCard && !uploadingFiles.aadharCard && (
                            <span className="file-success-text">
                              <i className="fas fa-check-circle"></i> {typeof formData.aadharCard === 'string' ? 'Aadhar Card uploaded' : formData.aadharCard.name}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="panCard" className="form-label">
                          PAN Card<span className="required-star">*</span>
                        </label>
                        <div className="file-input-wrapper">
                          <input
                            type="file"
                            id="panCard"
                            name="panCard"
                            className="form-control file-input-padding"
                            accept="image/*,.pdf"
                            disabled={uploadingFiles.aadharCard || uploadingFiles.panCard || Object.values(uploadingFiles.educationCertificates).some(uploading => uploading) || !formData.aadharCard}
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (file) {
                                try {
                                  // Upload file immediately and store S3 key
                                  const s3Key = await uploadFile(file, 'panCard');
                                  setFormData(prev => ({ ...prev, panCard: s3Key }));
                                  toast.success('PAN Card uploaded successfully');
                                } catch (error) {
                                  // Reset file input on error
                                  e.target.value = '';
                                  setFormData(prev => ({ ...prev, panCard: null }));
                                }
                              }
                            }}
                          />
                          {uploadingFiles.panCard && (
                            <span className="file-uploading-text">
                              <i className="fas fa-spinner fa-spin"></i> Uploading PAN Card...
                            </span>
                          )}
                          {formData.panCard && !uploadingFiles.panCard && (
                            <span className="file-success-text">
                              <i className="fas fa-check-circle"></i> {typeof formData.panCard === 'string' ? 'PAN Card uploaded' : formData.panCard.name}
                            </span>
                          )}
                          {!formData.aadharCard && !uploadingFiles.panCard && (
                            <small className="form-text text-muted" style={{ display: 'block', marginTop: '5px' }}>
                              Please upload Aadhar Card first
                            </small>
                          )}
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">
                          Education Certificates <span className="required-star">*</span>
                        </label>
                        <div className="file-input-wrapper">
                          <input
                            type="file"
                            className="form-control file-input-padding"
                            accept="image/*,.pdf"
                            multiple
                            disabled={uploadingFiles.aadharCard || uploadingFiles.panCard || Object.values(uploadingFiles.educationCertificates).some(uploading => uploading) || !formData.aadharCard || !formData.panCard}
                            onChange={async (e) => {
                              const files = Array.from(e.target.files);
                              if (files.length > 0) {
                                try {
                                  const currentCount = formData.educationCertificates.length;
                                  // Upload files sequentially (one at a time)
                                  const s3Keys = [];
                                  for (let i = 0; i < files.length; i++) {
                                    const file = files[i];
                                    const index = currentCount + i;
                                    try {
                                      const s3Key = await uploadFile(file, 'educationCertificate', index);
                                      s3Keys.push(s3Key);
                                      // Update form data after each successful upload
                                      setFormData(prev => ({
                                        ...prev,
                                        educationCertificates: [...prev.educationCertificates, s3Key]
                                      }));
                                      // toast.success(`Education Certificate ${i + 1} uploaded successfully`);
                                    } catch (error) {
                                      toast.error(`Failed to upload certificate ${i + 1}`);
                                      // Continue with next file even if one fails
                                    }
                                  }
                                  
                                  if (s3Keys.length > 0) {
                                    toast.success(`${s3Keys.length} education certificate(s) uploaded successfully`);
                                  }
                                } catch (error) {
                                  // Reset file input on error
                                  e.target.value = '';
                                  toast.error('Failed to upload education certificates');
                                }
                              }
                            }}
                          />
                          {Object.values(uploadingFiles.educationCertificates).some(uploading => uploading) && (
                            <span className="file-uploading-text">
                              <i className="fas fa-spinner fa-spin"></i> Uploading Education Certificate...
                            </span>
                          )}
                          {formData.educationCertificates.length > 0 && (
                            <div className="file-list-container">
                              {formData.educationCertificates.map((fileOrKey, index) => (
                                <div key={index} className="file-list-item">
                                  <i className="fas fa-check-circle"></i> {typeof fileOrKey === 'string' ? `Certificate ${index + 1} uploaded` : fileOrKey.name}
                                  {uploadingFiles.educationCertificates[index] && (
                                    <span className="ml-2">
                                      <i className="fas fa-spinner fa-spin"></i> Uploading...
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setFormData(prev => ({
                                        ...prev,
                                        educationCertificates: prev.educationCertificates.filter((_, i) => i !== index)
                                      }));
                                    }}
                                    className="btn-remove-file"
                                    disabled={uploadingFiles.aadharCard || uploadingFiles.panCard || Object.values(uploadingFiles.educationCertificates).some(uploading => uploading)}
                                  >
                                    <i className="fas fa-times"></i>
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          {(!formData.aadharCard || !formData.panCard) && !Object.values(uploadingFiles.educationCertificates).some(uploading => uploading) && (
                            <small className="form-text text-muted" style={{ display: 'block', marginTop: '5px' }}>
                              Please upload Aadhar Card and PAN Card first
                            </small>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="form-actions">
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => setActiveTab('bank')}
                      >
                        Previous
                      </button>
                      <button
                        type="submit"
                        className={`btn-primary ${!areRequiredDocumentsUploaded() ? 'btn-disabled' : 'btn-enabled'}`}
                        disabled={loading || !areRequiredDocumentsUploaded()}
                      >
                        {loading ? (
                          <>
                            <i className="fas fa-spinner fa-spin"></i>
                            <span>Submitting...</span>
                          </>
                        ) : (
                          <>
                            <span>Complete Profile</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Address Modal */}
      {showAddressModal && (
        <div className="modal-overlay" onClick={handleCloseAddressModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title">Employee Address</h5>
              <button
                type="button"
                className="modal-close"
                onClick={handleCloseAddressModal}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
              <form onSubmit={handleSaveAddress}>
                {/* Permanent Address Section */}
                <div className="mb-4">
                  <h6 className="mb-3" style={{ color: '#495057', fontWeight: '600', borderBottom: '2px solid #007bff', paddingBottom: '8px' }}>
                    Permanent Address
                  </h6>

                  {/* Address 1 and Address 2 - Side by Side */}
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="permanent.address1" className="form-label">
                          Address 1<span className="required-star">*</span>
                        </label>
                        <input
                          type="text"
                          id="permanent.address1"
                          name="permanent.address1"
                          className={`form-control ${addressErrors['permanent.address1'] ? 'is-invalid' : ''}`}
                          value={addressData.permanent.address1}
                          onChange={handleAddressChange}
                          placeholder="Enter address line 1"
                        />
                        {addressErrors['permanent.address1'] && (
                          <div className="invalid-feedback">{addressErrors['permanent.address1']}</div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="permanent.address2" className="form-label">Address 2</label>
                        <input
                          type="text"
                          id="permanent.address2"
                          name="permanent.address2"
                          className="form-control"
                          value={addressData.permanent.address2}
                          onChange={handleAddressChange}
                          placeholder="Enter address line 2"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Country and State - Side by Side */}
                  <div className="row">
                    <div className={`col-md-6 ${showCountryDropdown && activeCountryDropdown === 'permanent' ? 'modal-col-z-index-9999' : (showStateDropdown && activeStateDropdown === 'permanent' ? 'modal-col-z-index-1' : 'modal-col-z-index-auto')}`}>
                      <div className="form-group">
                        <label htmlFor="permanent.country" className="form-label">
                          Country<span className="required-star">*</span>
                        </label>
                        <div className={`custom-dropdown-wrapper ${showCountryDropdown && activeCountryDropdown === 'permanent' ? 'active' : ''}`} ref={countryDropdownRef}>
                          <div
                            className={`custom-dropdown ${addressErrors['permanent.country'] ? 'is-invalid' : ''} ${showCountryDropdown && activeCountryDropdown === 'permanent' ? 'active' : ''}`}
                            onClick={() => {
                              if (!showCountryDropdown || activeCountryDropdown !== 'permanent') {
                                setShowCountryDropdown(true);
                                setActiveCountryDropdown('permanent');
                                setCountrySearchTerm('');
                              }
                            }}
                            tabIndex={0}
                            onKeyDown={showCountryDropdown && activeCountryDropdown === 'permanent' ? undefined : (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setShowCountryDropdown(true);
                                setActiveCountryDropdown('permanent');
                                setCountrySearchTerm('');
                              }
                            }}
                          >
                            {showCountryDropdown && activeCountryDropdown === 'permanent' ? (
                              <input
                                type="text"
                                className="custom-dropdown-input"
                                value={countrySearchTerm}
                                onChange={(e) => {
                                  setCountrySearchTerm(e.target.value);
                                }}
                                onKeyDown={(e) => handleCountryKeyDown(e, 'permanent')}
                                placeholder="Search country..."
                                autoFocus
                              />
                            ) : (
                              <div className="custom-dropdown-display" tabIndex={-1}>
                                {addressData.permanent.country || 'Select Country'}
                              </div>
                            )}
                            <i
                              className={`fas fa-chevron-${showCountryDropdown && activeCountryDropdown === 'permanent' ? 'up' : 'down'} custom-dropdown-arrow`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (showCountryDropdown && activeCountryDropdown === 'permanent') {
                                  setShowCountryDropdown(false);
                                  setActiveCountryDropdown(null);
                                } else {
                                  setShowCountryDropdown(true);
                                  setActiveCountryDropdown('permanent');
                                  setCountrySearchTerm('');
                                }
                              }}
                            ></i>
                          </div>
                          {showCountryDropdown && activeCountryDropdown === 'permanent' && (
                            <div className="custom-dropdown-list">
                              <div className="custom-dropdown-options">
                                {filteredCountries.length > 0 ? (
                                  filteredCountries.map((country) => (
                                    <div
                                      key={country}
                                      className={`custom-dropdown-option ${addressData.permanent.country === country ? 'selected' : ''}`}
                                      onClick={() => handleCountrySelect(country, 'permanent')}
                                    >
                                      {country}
                                    </div>
                                  ))
                                ) : (
                                  <div className="custom-dropdown-option no-results">No countries found</div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        {addressErrors['permanent.country'] && (
                          <div className="invalid-feedback">{addressErrors['permanent.country']}</div>
                        )}
                      </div>
                    </div>
                    <div className={`col-md-6 ${showStateDropdown && activeStateDropdown === 'permanent' ? 'modal-col-z-index-9999' : 'modal-col-z-index-auto'}`}>
                      <div className="form-group">
                        <label htmlFor="permanent.state" className="form-label">
                          State<span className="required-star">*</span>
                        </label>
                        <div className={`custom-dropdown-wrapper ${showStateDropdown && activeStateDropdown === 'permanent' ? 'active' : ''}`} ref={stateDropdownRef}>
                          <div
                            className={`custom-dropdown ${addressErrors['permanent.state'] ? 'is-invalid' : ''} ${showStateDropdown && activeStateDropdown === 'permanent' ? 'active' : ''}`}
                            onClick={() => {
                              if (!showStateDropdown || activeStateDropdown !== 'permanent') {
                                setShowStateDropdown(true);
                                setActiveStateDropdown('permanent');
                                setStateSearchTerm('');
                              }
                            }}
                            tabIndex={0}
                            onKeyDown={showStateDropdown && activeStateDropdown === 'permanent' ? undefined : (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setShowStateDropdown(true);
                                setActiveStateDropdown('permanent');
                                setStateSearchTerm('');
                              }
                            }}
                          >
                            {showStateDropdown && activeStateDropdown === 'permanent' ? (
                              <input
                                type="text"
                                className="custom-dropdown-input"
                                value={stateSearchTerm}
                                onChange={(e) => {
                                  setStateSearchTerm(e.target.value);
                                }}
                                onKeyDown={(e) => handleStateKeyDown(e, 'permanent')}
                                placeholder="Search state..."
                                autoFocus
                              />
                            ) : (
                              <div className="custom-dropdown-display" tabIndex={-1}>
                                {addressData.permanent.state || 'Select State'}
                              </div>
                            )}
                            <i
                              className={`fas fa-chevron-${showStateDropdown && activeStateDropdown === 'permanent' ? 'up' : 'down'} custom-dropdown-arrow`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (showStateDropdown && activeStateDropdown === 'permanent') {
                                  setShowStateDropdown(false);
                                  setActiveStateDropdown(null);
                                } else {
                                  setShowStateDropdown(true);
                                  setActiveStateDropdown('permanent');
                                  setStateSearchTerm('');
                                }
                              }}
                            ></i>
                          </div>
                          {showStateDropdown && activeStateDropdown === 'permanent' && (
                            <div className="custom-dropdown-list">
                              <div className="custom-dropdown-options">
                                {filteredStates.length > 0 ? (
                                  filteredStates.map((state) => (
                                    <div
                                      key={state}
                                      className={`custom-dropdown-option ${addressData.permanent.state === state ? 'selected' : ''}`}
                                      onClick={() => handleStateSelect(state, 'permanent')}
                                    >
                                      {state}
                                    </div>
                                  ))
                                ) : (
                                  <div className="custom-dropdown-option no-results">No states found</div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        {addressErrors['permanent.state'] && (
                          <div className="invalid-feedback">{addressErrors['permanent.state']}</div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* City and Pincode - Side by Side */}
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="permanent.city" className="form-label">
                          City<span className="required-star">*</span>
                        </label>
                        <input
                          type="text"
                          id="permanent.city"
                          name="permanent.city"
                          className={`form-control ${addressErrors['permanent.city'] ? 'is-invalid' : ''}`}
                          value={addressData.permanent.city}
                          onChange={handleAddressChange}
                          placeholder="Enter city"
                        />
                        {addressErrors['permanent.city'] && (
                          <div className="invalid-feedback">{addressErrors['permanent.city']}</div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="permanent.pincode" className="form-label">
                          Pincode<span className="required-star">*</span>
                        </label>
                        <input
                          type="text"
                          id="permanent.pincode"
                          name="permanent.pincode"
                          className={`form-control ${addressErrors['permanent.pincode'] ? 'is-invalid' : ''}`}
                          value={addressData.permanent.pincode}
                          onChange={handlePincodeChange}
                          placeholder="Enter 6-digit pincode"
                          maxLength="6"
                          inputMode="numeric"
                        />
                        {addressErrors['permanent.pincode'] && (
                          <div className="invalid-feedback">{addressErrors['permanent.pincode']}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Checkbox for same as permanent */}
                <div className="form-group mt-3 mb-4">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      id="sameAsPermanent"
                      name="sameAsPermanent"
                      className="form-check-input"
                      checked={addressData.sameAsPermanent}
                      onChange={handleAddressChange}
                    />
                    <label className="form-check-label" htmlFor="sameAsPermanent">
                      Present address same as Permanent address
                    </label>
                  </div>
                </div>

                {/* Present Address Section - Hidden when sameAsPermanent is checked */}
                {!addressData.sameAsPermanent && (
                  <div className="mb-4">
                    <h6 className="mb-3" style={{ color: '#495057', fontWeight: '600', borderBottom: '2px solid #28a745', paddingBottom: '8px' }}>
                      Present Address
                    </h6>

                    {/* Address 1 and Address 2 - Side by Side */}
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label htmlFor="present.address1" className="form-label">
                            Address 1<span className="required-star">*</span>
                          </label>
                          <input
                            type="text"
                            id="present.address1"
                            name="present.address1"
                            className={`form-control ${addressErrors['present.address1'] ? 'is-invalid' : ''}`}
                            value={addressData.present.address1}
                            onChange={handleAddressChange}
                            placeholder="Enter address line 1"
                          />
                          {addressErrors['present.address1'] && (
                            <div className="invalid-feedback">{addressErrors['present.address1']}</div>
                          )}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label htmlFor="present.address2" className="form-label">Address 2</label>
                          <input
                            type="text"
                            id="present.address2"
                            name="present.address2"
                            className="form-control"
                            value={addressData.present.address2}
                            onChange={handleAddressChange}
                            placeholder="Enter address line 2"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Country and State - Side by Side */}
                    <div className="row">
                      <div className={`col-md-6 ${showCountryDropdown && activeCountryDropdown === 'present' ? 'modal-col-z-index-9999' : (showStateDropdown && activeStateDropdown === 'present' ? 'modal-col-z-index-1' : 'modal-col-z-index-auto')}`}>
                        <div className="form-group">
                          <label htmlFor="present.country" className="form-label">
                            Country<span className="required-star">*</span>
                          </label>
                          <div className={`custom-dropdown-wrapper ${showCountryDropdown && activeCountryDropdown === 'present' ? 'active' : ''}`} ref={countryDropdownRef}>
                            <div
                              className={`custom-dropdown ${addressErrors['present.country'] ? 'is-invalid' : ''} ${showCountryDropdown && activeCountryDropdown === 'present' ? 'active' : ''}`}
                              onClick={() => {
                                if (!showCountryDropdown || activeCountryDropdown !== 'present') {
                                  setShowCountryDropdown(true);
                                  setActiveCountryDropdown('present');
                                  setCountrySearchTerm('');
                                }
                              }}
                              tabIndex={0}
                              onKeyDown={showCountryDropdown && activeCountryDropdown === 'present' ? undefined : (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setShowCountryDropdown(true);
                                  setActiveCountryDropdown('present');
                                  setCountrySearchTerm('');
                                }
                              }}
                            >
                              {showCountryDropdown && activeCountryDropdown === 'present' ? (
                                <input
                                  type="text"
                                  className="custom-dropdown-input"
                                  value={countrySearchTerm}
                                  onChange={(e) => {
                                    setCountrySearchTerm(e.target.value);
                                  }}
                                  onKeyDown={(e) => handleCountryKeyDown(e, 'present')}
                                  placeholder="Search country..."
                                  autoFocus
                                />
                              ) : (
                                <div className="custom-dropdown-display" tabIndex={-1}>
                                  {addressData.present.country || 'Select Country'}
                                </div>
                              )}
                              <i
                                className={`fas fa-chevron-${showCountryDropdown && activeCountryDropdown === 'present' ? 'up' : 'down'} custom-dropdown-arrow`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (showCountryDropdown && activeCountryDropdown === 'present') {
                                    setShowCountryDropdown(false);
                                    setActiveCountryDropdown(null);
                                  } else {
                                    setShowCountryDropdown(true);
                                    setActiveCountryDropdown('present');
                                    setCountrySearchTerm('');
                                  }
                                }}
                              ></i>
                            </div>
                            {showCountryDropdown && activeCountryDropdown === 'present' && (
                              <div className="custom-dropdown-list">
                                <div className="custom-dropdown-options">
                                  {filteredCountries.length > 0 ? (
                                    filteredCountries.map((country) => (
                                      <div
                                        key={country}
                                        className={`custom-dropdown-option ${addressData.present.country === country ? 'selected' : ''}`}
                                        onClick={() => handleCountrySelect(country, 'present')}
                                      >
                                        {country}
                                      </div>
                                    ))
                                  ) : (
                                    <div className="custom-dropdown-option no-results">No countries found</div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                          {addressErrors['present.country'] && (
                            <div className="invalid-feedback">{addressErrors['present.country']}</div>
                          )}
                        </div>
                      </div>
                      <div className={`col-md-6 ${showStateDropdown && activeStateDropdown === 'present' ? 'modal-col-z-index-9999' : 'modal-col-z-index-auto'}`}>
                        <div className="form-group">
                          <label htmlFor="present.state" className="form-label">
                            State<span className="required-star">*</span>
                          </label>
                          <div className={`custom-dropdown-wrapper ${showStateDropdown && activeStateDropdown === 'present' ? 'active' : ''}`} ref={stateDropdownRef}>
                            <div
                              className={`custom-dropdown ${addressErrors['present.state'] ? 'is-invalid' : ''} ${showStateDropdown && activeStateDropdown === 'present' ? 'active' : ''}`}
                              onClick={() => {
                                if (!showStateDropdown || activeStateDropdown !== 'present') {
                                  setShowStateDropdown(true);
                                  setActiveStateDropdown('present');
                                  setStateSearchTerm('');
                                }
                              }}
                              tabIndex={0}
                              onKeyDown={showStateDropdown && activeStateDropdown === 'present' ? undefined : (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setShowStateDropdown(true);
                                  setActiveStateDropdown('present');
                                  setStateSearchTerm('');
                                }
                              }}
                            >
                              {showStateDropdown && activeStateDropdown === 'present' ? (
                                <input
                                  type="text"
                                  className="custom-dropdown-input"
                                  value={stateSearchTerm}
                                  onChange={(e) => {
                                    setStateSearchTerm(e.target.value);
                                  }}
                                  onKeyDown={(e) => handleStateKeyDown(e, 'present')}
                                  placeholder="Search state..."
                                  autoFocus
                                />
                              ) : (
                                <div className="custom-dropdown-display" tabIndex={-1}>
                                  {addressData.present.state || 'Select State'}
                                </div>
                              )}
                              <i
                                className={`fas fa-chevron-${showStateDropdown && activeStateDropdown === 'present' ? 'up' : 'down'} custom-dropdown-arrow`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (showStateDropdown && activeStateDropdown === 'present') {
                                    setShowStateDropdown(false);
                                    setActiveStateDropdown(null);
                                  } else {
                                    setShowStateDropdown(true);
                                    setActiveStateDropdown('present');
                                    setStateSearchTerm('');
                                  }
                                }}
                              ></i>
                            </div>
                            {showStateDropdown && activeStateDropdown === 'present' && (
                              <div className="custom-dropdown-list">
                                <div className="custom-dropdown-options">
                                  {filteredStates.length > 0 ? (
                                    filteredStates.map((state) => (
                                      <div
                                        key={state}
                                        className={`custom-dropdown-option ${addressData.present.state === state ? 'selected' : ''}`}
                                        onClick={() => handleStateSelect(state, 'present')}
                                      >
                                        {state}
                                      </div>
                                    ))
                                  ) : (
                                    <div className="custom-dropdown-option no-results">No states found</div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                          {addressErrors['present.state'] && (
                            <div className="invalid-feedback">{addressErrors['present.state']}</div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* City and Pincode - Side by Side */}
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label htmlFor="present.city" className="form-label">
                            City<span className="required-star">*</span>
                          </label>
                          <input
                            type="text"
                            id="present.city"
                            name="present.city"
                            className={`form-control ${addressErrors['present.city'] ? 'is-invalid' : ''}`}
                            value={addressData.present.city}
                            onChange={handleAddressChange}
                            placeholder="Enter city"
                          />
                          {addressErrors['present.city'] && (
                            <div className="invalid-feedback">{addressErrors['present.city']}</div>
                          )}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label htmlFor="present.pincode" className="form-label">
                            Pincode<span className="required-star">*</span>
                          </label>
                          <input
                            type="text"
                            id="present.pincode"
                            name="present.pincode"
                            className={`form-control ${addressErrors['present.pincode'] ? 'is-invalid' : ''}`}
                            value={addressData.present.pincode}
                            onChange={handlePincodeChange}
                            placeholder="Enter 6-digit pincode"
                            maxLength="6"
                            inputMode="numeric"
                          />
                          {addressErrors['present.pincode'] && (
                            <div className="invalid-feedback">{addressErrors['present.pincode']}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleCloseAddressModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveAddressButton}
                    className="btn-primary"
                  >
                    Save
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default EmployeeSelf;  